import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAuthService from '../../../app/services/hooks/useAuthService';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import InputField from '../../../components/InputField';
import LazyLoading from '../../../components/LazyLoading';
import logo_monere from '../../../assets/images/logo_monere.png';
import './ActivateAccount.css';

const ActivateAccount: React.FC = () => {
    const history = useHistory();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { fetchingActivateAccount, activateAccount, fetchingValidateToken, validateActivationToken } = useAuthService();
    
    const [token, setToken] = useState<string>('');
    const [tokenValid, setTokenValid] = useState<boolean | null>(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [activationSuccess, setActivationSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        // Obtener el token de la URL
        const params = new URLSearchParams(window.location.search);
        const tokenParam = params.get('token');
        
        if (tokenParam) {
            setToken(tokenParam);
            // Validar el token
            validateActivationToken(tokenParam, {
                onSuccess: (response: ServiceResponse) => {
                    setTokenValid(true);
                },
                onError: (response: ServiceResponse) => {
                    setTokenValid(false);
                    setErrorMessage(response?.message || 'El token de activación es inválido o ha expirado.');
                }
            });
        } else {
            setTokenValid(false);
            setErrorMessage('No se proporcionó un token de activación válido.');
        }
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setErrors({});
        setErrorMessage('');

        // Validaciones locales
        if (password.length < 8) {
            setErrors({ password: ['La contraseña debe tener al menos 8 caracteres'] });
            return;
        }

        if (password !== confirmPassword) {
            setErrors({ confirm_password: ['Las contraseñas no coinciden'] });
            return;
        }

        // Activar cuenta
        activateAccount(token, password, confirmPassword, {
            onSuccess: (response: ServiceResponse) => {
                setActivationSuccess(true);
                toast.success('¡Cuenta activada exitosamente!');
                setTimeout(() => {
                    history.push('/login');
                }, 3000);
            },
            onError: (response: ServiceResponse) => {
                setErrorMessage(response?.message || 'Error al activar la cuenta. Intenta nuevamente.');
                toast.error(response?.message || 'Error al activar la cuenta');
            },
            onFieldError: (response: ServiceResponse) => {
                setErrors(response.data);
                toast.error('Por favor, corrige los errores en el formulario');
            }
        });
    };

    // Mientras se valida el token
    if (tokenValid === null) {
        return (
            <div className="login-container">
                <div className="login-background">
                    <div className="login-background-pattern"></div>
                </div>
                <div className="login-content">
                    <div className="login-card">
                        <div className="login-loading">
                            <LazyLoading height={200} />
                            <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '16px' }}>
                                Validando token de activación...
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Si el token es inválido
    if (tokenValid === false) {
        return (
            <div className="login-container">
                <div className="login-background">
                    <div className="login-background-pattern"></div>
                </div>
                <div className="login-content">
                    <div className="login-card">
                        <div className="activation-error">
                            <i className="fas fa-times-circle error-icon"></i>
                            <h2 className="error-title">Token Inválido</h2>
                            <p className="error-message">{errorMessage}</p>
                            <button
                                className="btn-login"
                                onClick={() => history.push('/login')}
                            >
                                <span>Ir al Login</span>
                                <i className="fas fa-arrow-right"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Si la activación fue exitosa
    if (activationSuccess) {
        return (
            <div className="login-container">
                <div className="login-background">
                    <div className="login-background-pattern"></div>
                </div>
                <div className="login-content">
                    <div className="login-card">
                        <div className="activation-success">
                            <i className="fas fa-check-circle success-icon"></i>
                            <h2 className="success-title">¡Cuenta Activada!</h2>
                            <p className="success-message">
                                Tu cuenta ha sido activada exitosamente. Serás redirigido al login en unos segundos...
                            </p>
                            <div className="loading-redirect">
                                <LazyLoading height={50} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Formulario de activación
    return (
        <div className="login-container">
            <div className="login-background">
                <div className="login-background-pattern"></div>
            </div>
            
            <div className="login-content">
                <div className="login-card">
                    <div className="login-header">
                        <div className="login-logo">
                            <img src={logo_monere} alt="Monere" />
                        </div>
                        <h1 className="login-title">Activa tu Cuenta</h1>
                        <p className="login-subtitle">Crea una contraseña segura para acceder a tu cuenta</p>
                    </div>

                    {!fetchingActivateAccount ? (
                        <div className="login-body">
                            {errorMessage && (
                                <div className="alert alert-error">
                                    <i className="fas fa-exclamation-triangle"></i>
                                    <span>{errorMessage}</span>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="login-form">
                                <InputField
                                    type="password"
                                    label="Nueva Contraseña"
                                    value={password}
                                    onChange={(value) => setPassword(value)}
                                    onFocus={() => onFocusRemove('password')}
                                    placeholder="Mínimo 8 caracteres"
                                    error={fieldErrorMessage('password')}
                                    icon="fas fa-lock"
                                    backgroundColor="#FAFBFC"
                                    required
                                />

                                <InputField
                                    type="password"
                                    label="Confirmar Contraseña"
                                    value={confirmPassword}
                                    onChange={(value) => setConfirmPassword(value)}
                                    onFocus={() => onFocusRemove('confirm_password')}
                                    placeholder="Repite tu contraseña"
                                    error={fieldErrorMessage('confirm_password')}
                                    icon="fas fa-lock"
                                    backgroundColor="#FAFBFC"
                                    required
                                />

                                <button type="submit" className="btn-login">
                                    <span>Activar Cuenta</span>
                                    <i className="fas fa-check"></i>
                                </button>

                                <div className="login-footer">
                                    <button
                                        type="button"
                                        className="btn-link"
                                        onClick={() => history.push('/login')}
                                    >
                                        ¿Ya tienes cuenta? Inicia sesión
                                    </button>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="login-loading">
                            <LazyLoading height={200} />
                            <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '16px' }}>
                                Activando tu cuenta...
                            </p>
                        </div>
                    )}
                </div>

                <div className="login-promo">
                    <div className="promo-content">
                        <h2 className="promo-title">
                            ¡Bienvenido a Monere!
                        </h2>
                        <p className="promo-text">
                            Gestiona tus solicitudes de registro de marca de forma simple y eficiente.
                        </p>
                        <div className="promo-features">
                            <div className="feature-item">
                                <i className="fas fa-user-check"></i>
                                <span>Activación Segura</span>
                            </div>
                            <div className="feature-item">
                                <i className="fas fa-shield-alt"></i>
                                <span>Datos Protegidos</span>
                            </div>
                            <div className="feature-item">
                                <i className="fas fa-bell"></i>
                                <span>Notificaciones en Tiempo Real</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActivateAccount;

